import React, { useEffect, useState } from "react";
import Preview from "./components/Preview";
import Playground from "./components/Playground";
import LMSPlayground from "./components/LMSPlayground";
import Navbar from "./components/Navbar";
import JSZip from "jszip";
import myGif from './assets/loading.gif';
import "./App.css";

const LoadingSpinner = () => (
  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <div className="flex flex-col items-center gap-3">
      <img src={myGif} alt="My animation" className="w-[180px] h-[80px] object-contain" />
      <span className="text-sm text-gray-600">Loading...</span>
    </div>
  </div>
);

function exportAsHtmlFile(html, css, js, userTitle) {
  // Extract title from HTML if provided by user, otherwise use default
  let pageTitle = userTitle || "Exported Project";
  
  // Try to extract title from HTML if not provided
  if (!userTitle) {
    const titleMatch = html.match(/<title[^>]*>(.*?)<\/title>/i);
    if (titleMatch && titleMatch[1]) {
      pageTitle = titleMatch[1].trim();
    }
  }

  // Create safe filename from title
  const safeFilename = pageTitle
    .replace(/[^a-zA-Z0-9\s-_]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50)
    .toLowerCase() || 'exported_project';

  // Inject CSS and JS into the HTML
  let content = html;
  
  // Update or add title if user provided one
  if (userTitle) {
    const titleRegex = /<title[^>]*>.*?<\/title>/i;
    if (titleRegex.test(content)) {
      content = content.replace(titleRegex, `<title>${userTitle}</title>`);
    } else {
      // Add title in head section or before closing body
      if (content.includes('</head>')) {
        content = content.replace('</head>', `  <title>${userTitle}</title>\n</head>`);
      } else if (content.includes('<head>')) {
        content = content.replace('<head>', `<head>\n  <title>${userTitle}</title>`);
      } else {
        // If no head section, add a minimal one
        const titleTag = `<head><title>${userTitle}</title></head>`;
        if (content.includes('<html>')) {
          content = content.replace('<html>', `<html>\n${titleTag}`);
        } else {
          content = `${titleTag}\n${content}`;
        }
      }
    }
  }
  
  // Add CSS before </head> if it exists, otherwise before </body> or at the end
  // Preserve any existing <link> tags that might be in the HTML
  if (css) {
    const styleTag = `<style>\n${css}\n</style>`;
    if (content.includes('</head>')) {
      content = content.replace('</head>', `${styleTag}\n</head>`);
    } else if (content.includes('</body>')) {
      content = content.replace('</body>', `${styleTag}\n</body>`);
    } else {
      content += `\n${styleTag}`;
    }
  }
  
  // Add JS before </body> if it exists, otherwise at the end
  if (js) {
    const scriptTag = `<script>\n${js}\n</script>`;
    if (content.includes('</body>')) {
      content = content.replace('</body>', `${scriptTag}\n</body>`);
    } else {
      content += `\n${scriptTag}`;
    }
  }

  const blob = new Blob([content], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeFilename}.html`;
  a.click();

  URL.revokeObjectURL(url);
}

async function exportAsZip(html, css, js, userTitle) {
  const zip = new JSZip();

  // Extract title from HTML if provided by user, otherwise use default
  let pageTitle = userTitle || "Exported Project";
  
  // Try to extract title from HTML if not provided
  if (!userTitle) {
    const titleMatch = html.match(/<title[^>]*>(.*?)<\/title>/i);
    if (titleMatch && titleMatch[1]) {
      pageTitle = titleMatch[1].trim();
    }
  }

  // Create safe filename from title (remove special characters, limit length)
  const safeFilename = pageTitle
    .replace(/[^a-zA-Z0-9\s-_]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50)
    .toLowerCase() || 'exported_project';

  // For ZIP export, we need to handle two cases:
  // 1. If HTML already has full structure, add external links
  // 2. If HTML is just body content, wrap it
  
  let fullHtml = html;
  
  // Check if HTML already has full document structure
  const hasDoctype = html.toLowerCase().includes('<!doctype');
  const hasHtmlTag = html.toLowerCase().includes('<html');
  
  if (hasDoctype || hasHtmlTag) {
    // HTML has full structure - preserve existing title and links
    // Update title if user provided one and it's different
    if (userTitle) {
      const titleRegex = /<title[^>]*>.*?<\/title>/i;
      if (titleRegex.test(fullHtml)) {
        fullHtml = fullHtml.replace(titleRegex, `<title>${userTitle}</title>`);
      } else {
        // Add title if none exists
        fullHtml = fullHtml.replace('</head>', `  <title>${userTitle}</title>\n</head>`);
      }
    }
    
    // Add external CSS/JS links only if they don't exist and we have CSS/JS
    if (css && !html.includes('href="style.css"')) {
      fullHtml = fullHtml.replace('</head>', '  <link rel="stylesheet" href="style.css" />\n</head>');
    }
    if (js && !html.includes('src="script.js"')) {
      fullHtml = fullHtml.replace('</body>', '  <script src="script.js"></script>\n</body>');
    }
  } else {
    // HTML is just content - wrap it with full structure
    // Extract any existing links from the HTML content to preserve them
    const linkMatches = html.match(/<link[^>]*>/gi) || [];
    const existingLinks = linkMatches.join('\n  ');
    
    fullHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${pageTitle}</title>
  ${existingLinks ? `  ${existingLinks}` : ''}
  <link rel="stylesheet" href="style.css" />
</head>
<body>
${html}
<script src="script.js"></script>
</body>
</html>`;
  }

  zip.file("index.html", fullHtml || "");
  zip.file("style.css", css || "");
  zip.file("script.js", js || "");

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeFilename}.zip`;
  a.click();

  URL.revokeObjectURL(url);
}

async function importHtmlFile(
    file,
    setLoading
  ){
  setLoading(true);
  const text = await file.text();

  // Extract CSS from <style> tags
  const styleMatches = [...text.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)];
  const extractedCss = styleMatches.map((m) => m[1].trim()).join("\n");
  
  // Extract JS from <script> tags
  const scriptMatches = [...text.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi)];
  const extractedJs = scriptMatches.map((m) => m[1].trim()).join("\n");
  
  // Remove CSS and JS from HTML but keep the full HTML structure
  let cleanHtml = text
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .trim();

  setLoading(false);

  return {
    html: cleanHtml,
    css: extractedCss,
    js: extractedJs,
  };
}

async function importZipFile(file, setLoading) {
  try {
    setLoading(true);
    const zip = await JSZip.loadAsync(file);

    // Read each file (if present)
    const htmlFile = zip.file("index.html");
    const cssFile = zip.file("style.css");
    const jsFile = zip.file("script.js");

    const htmlRaw = htmlFile ? await htmlFile.async("string") : "";
    const css = cssFile ? await cssFile.async("string") : "";
    const js = jsFile ? await jsFile.async("string") : "";

    // Extract inline CSS/JS from HTML
    const styleMatches = [...htmlRaw.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)];
    const scriptMatches = [...htmlRaw.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi)];

    const inlineCss = styleMatches.map((m) => m[1].trim()).join("\n");
    const inlineJs = scriptMatches.map((m) => m[1].trim()).join("\n");

    // Remove inline <style>, <script>, and external links from HTML
    let cleanHtml = htmlRaw
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<link[^>]*rel=["']stylesheet["'][^>]*>/gi, "")
      .replace(/<script[^>]*src=["']script\.js["'][^>]*><\/script>/gi, "")
      .trim();

    // Combine external + inline CSS/JS
    const combinedCss = [css, inlineCss].filter(Boolean).join("\n");
    const combinedJs = [js, inlineJs].filter(Boolean).join("\n");

    return {
      html: cleanHtml,
      css: combinedCss,
      js: combinedJs,
    };
  } finally {
    setLoading(false);
  }
}

export default function HTMLEditor({ isOpen, onClose }) {
  const [html, setHtml] = useState(`
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Fun Code World</title>
  <link rel="stylesheet" href="style.css">
</head>
<body>
  <div class="container">
    <h1>🌈 Welcome to Fun Code World! 🚀</h1>
    <p>Let's make something amazing today! 🎨✨</p>
    <button id="magicButton">Click Me!</button>
    <div id="surprise"></div>
  </div>
  <script src="script.js"></script>
</body>
</html>
`);
  const [css, setCss] = useState(`
body {
  margin: 0;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  background: linear-gradient(135deg, #FFDEE9, #B5FFFC);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.container {
  text-align: center;
  padding: 2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 20px rgba(0,0,0,0.2);
}

h1 {
  font-size: 2.5rem;
  color: #FF6F91;
}

p {
  font-size: 1.2rem;
  color: #333;
}

button {
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border: none;
  border-radius: 10px;
  background: #FF6F91;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

button:hover {
  transform: scale(1.1);
  background: #FF9AA2;
}

#surprise {
  margin-top: 1.5rem;
  font-size: 1.5rem;
  color: #FF6F91;
}
`);
  const [js, setJs] = useState(`
const button = document.getElementById('magicButton');
const surprise = document.getElementById('surprise');

button.addEventListener('click', () => {
  const emojis = ['✨', '🎉', '🌟', '🚀', '🎨', '🍭'];
  const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
  surprise.textContent = "Surprise! " + randomEmoji;
});
`);
  const [getPreviewUrlFn, setGetPreviewUrlFn] = useState(() => null);
  const [editorTheme, setEditorTheme] = useState("vs");
  const [sectionTab, setSectionTab] = useState(2);
  const [panelLayout, setPanelLayout] = useState("three-panel");

  const [lang, setLang] = useState("html");
  const [runKey, setRunKey] = useState(0);
  const [isPreviewMaximized, setIsPreviewMaximized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);

  useEffect(() => {
    let timeout = null; // Fix NodeJS type error
    if (isLoading) {
      timeout = setTimeout(() => setShowSpinner(true), 200); // show spinner only if loading > 200ms
    } else {
      setShowSpinner(false);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading]);

  const run = async () => {
    setRunKey((k) => k + 1);
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-indigo-100 via-blue-50 to-cyan-100 overflow-hidden">
      <div className="h-screen bg-gray-50 text-gray-900 flex flex-col">
        {showSpinner && <LoadingSpinner />}
        {/* Header */}
        <Navbar
          lang={lang}
          setLang={setLang}
          html={html}
          css={css}
          js={js}
          setHtml={setHtml}
          setCss={setCss}
          setJs={setJs}
          exportAsHtmlFile={exportAsHtmlFile}
          exportAsZip={exportAsZip}
          importHtmlFile={importHtmlFile}
          importZipFile={importZipFile}
          setIsLoading={setIsLoading}
          setIsPreviewMaximized={setIsPreviewMaximized}
          isProjectsOpen={isProjectsOpen}
          setIsProjectsOpen={setIsProjectsOpen}
          sectionTab={sectionTab}
          setSectionTab={setSectionTab}
          panelLayout={panelLayout}
          setPanelLayout={setPanelLayout}
          onClose={onClose}
        />
        {/* Maximized preview overlay */}
        {isPreviewMaximized && (
          <div className="fixed inset-0 z-50 bg-black/60 p-6 flex items-center justify-center">
            <div className="w-[100%] h-[100%] bg-white rounded-md shadow-lg overflow-hidden flex flex-col">
              {/* fake browser bar */}
              <div className="flex items-center justify-between px-4 py-2 bg-indigo-300 border-b">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <div className="w-2 h-2 rounded-full bg-yellow-400" />
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                  </div>
                  <div className="text-sm font-medium">example.local</div>
                  <div className="ml-4 text-xs text-gray-500">Preview</div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={run}
                    className="px-3 py-1 bg-emerald-600 text-white rounded text-sm"
                  >
                    Run
                  </button>
                  <button onClick={() => setIsPreviewMaximized(false)} className="p-2 rounded bg-gray-100">
                    <X />
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                <Preview key={`max-${runKey}`} html={html} css={css} js={js} runKey={runKey} getPreviewUrl={(fn) => setGetPreviewUrlFn(fn)} />
              </div>
            </div>
          </div>
        )}
        { sectionTab === 2 ? (
          <Playground
          setRunKey={setRunKey}
          importHtmlFile={importHtmlFile}
          setEditorTheme={setEditorTheme}
          html={html}
          css={css}
          js={js}
          setHtml={setHtml}
          setCss={setCss}
          setJs={setJs}
          setIsLoading={setIsLoading}
          lang={lang}
          setLang={setLang}
          editorTheme={editorTheme}
          runKey={runKey}
          setGetPreviewUrlFn={(fn) => setGetPreviewUrlFn(() => fn)}
          getPreviewUrlFn={getPreviewUrlFn}
          isProjectsOpen={isProjectsOpen}
          setIsProjectsOpen={setIsProjectsOpen}
        />
        ) : (
        <LMSPlayground
          setRunKey={setRunKey}
          html={html}
          css={css}
          js={js}
          setHtml={setHtml}
          setEditorTheme={setEditorTheme}
          setCss={setCss}
          setJs={setJs}
          editorTheme={editorTheme}
          runKey={runKey}
          setGetPreviewUrlFn={(fn) => setGetPreviewUrlFn(() => fn)}
          getPreviewUrlFn={getPreviewUrlFn}
          lang={lang}
          setLang={setLang}
          panelLayout={panelLayout}
          isProjectsOpen={isProjectsOpen}
          setIsProjectsOpen={setIsProjectsOpen}
        />
        )}
      </div>
    </div>
  );
}